import {
  Model,
  UICheckbox,
  UIField,
  UIIconButton,
  useEffectAsync,
  UIFormatTemplateSelection,
} from "@alphascriptsolutions/client-core";
import { useState } from "react";
import { message } from "antd";
import Renderer from "@/components/Renderer";
import { formatTemplatesTablePanelSchema } from "@/utils/sharePricePerformance/panelsSchema";
import { fetchPrimaryTemplateWithImage } from "@/utils/sharePricePerformance/fetchFormatTemplates";
import { getSelectedShapeName } from "@/utils/sharePricePerformance/selectedPlaceHolderLocalStorage";
import { useAlphaAgentStore } from "@/state/alphaAgentStore";
import { usePlaceholderPopupStore } from "@/state/placeholderPopupStore";
import { addFieldsToData, transformGlobalState } from "@/utils/alphaAgent/modelDataHelper";
import { getRenderAgentData } from "@/utils/alphaAgent/getRenderAgentData";
import { bufferToBase64ImagePrimaryTemplate } from "@/utils/formatTemplate/slides-extraction-handlers";
import { logger } from "@/helpers/tslog";
import FormatTemplateSearchPopup from "./FormatTemplateSearchPopup";

export default function FormatAndPlaceholder() {
  const { model, setModel } = useAlphaAgentStore();
  const { fromPlaceholderPopup } = usePlaceholderPopupStore();
  const [templateSearchVisible, setTemplateSearchVisible] = useState(false);
  const [currentPrimaryTemplate, setCurrentPrimaryTemplate] = useState<any>(null);

  useEffectAsync(async () => {
    const path = "/assets/alphaAgent/AlphaAgentFormatTemplate.yml";

    try {
      let loadedModel = await Model.fromFile(path);
      if (!loadedModel.isValid()) {
        throw new Error(`Problem loading model from ${path}: ${loadedModel.getError()}`);
      }

      await addEventsOnModel(loadedModel);
      setModel(loadedModel);
    } catch (err) {
      message.error(err.message);
    }
  }, []);

  const addEventsOnModel = async (model: Model) => {
    model.setSchema(formatTemplatesTablePanelSchema);

    const primaryFormatWithImage = await fetchPrimaryTemplateWithImage();
    const rawData = localStorage.getItem("ModelDataToBackend");
    const { formatAndPlaceholderDatasource } = await getRenderAgentData(rawData);

    const formatTemplateSource = model.getByName<UICheckbox>("formatTemplateSource");
    const formatTemplateNotes = model.getByName<UICheckbox>("formatTemplateNotes");
    formatTemplateSource.value = formatAndPlaceholderDatasource.formatTemplateSource ? ["Source"] : [];
    formatTemplateNotes.value = formatAndPlaceholderDatasource.formatTemplateNotes ? ["Notes"] : [];

    const presentationDesign = model.getByName("presentationDesign");
    if (presentationDesign) {
      try {
        // Check if there's a saved template selection
        const savedTemplate = localStorage.getItem("selectedFormatTemplate");
        let templateToShow = primaryFormatWithImage;
        let savedImage = null;

        if (savedTemplate) {
          try {
            const parsedTemplate = JSON.parse(savedTemplate);
            if (parsedTemplate.id && parsedTemplate.formatName) {
              // Use the saved template data for display
              templateToShow = {
                ...primaryFormatWithImage,
                id: parsedTemplate.id,
                formatName: parsedTemplate.formatName,
              };
              savedImage = parsedTemplate.image;
              logger.info("Restored saved template selection:", parsedTemplate.formatName);
            }
          } catch (parseError) {
            logger.error("Error parsing saved template:", parseError);
          }
        }

        if (templateToShow) {
          if (templateToShow.formatName) {
            (presentationDesign as any).templateName = templateToShow.formatName;
          }
          (presentationDesign as any).value = templateToShow.id;

          if (savedImage) {
            (presentationDesign as any).image = savedImage;
          } else if (templateToShow.frontCoverImage?.buffer) {
            try {
              (presentationDesign as any).image = bufferToBase64ImagePrimaryTemplate(
                templateToShow.frontCoverImage.buffer,
                templateToShow.frontCoverImage.contentType
              );
            } catch (e: any) {
              (presentationDesign as any).image = undefined;
            }
          } else {
            logger.info("No frontcover image found");
            (presentationDesign as any).image = undefined;
          }

          (presentationDesign as any).onClick = () => {
            setTemplateSearchVisible(true);
          };

          setCurrentPrimaryTemplate(templateToShow);
          localStorage.removeItem("selectedFormatTemplate");
        }
      } catch (error) {
        logger.error("Error populating format template selection:", error);
      }
    } else {
      logger.info("No presentationDesign component found");
    }

    const placeholderSelectionName = model.getByName<UIField>("placeholderSelectionName");
    const selectedPlaceholderName = getSelectedShapeName();
    placeholderSelectionName.value = selectedPlaceholderName ?? "";
    if (selectedPlaceholderName) {
      const store = useAlphaAgentStore.getState();
      store.markAnswered(store.currentStep);
    }

    const placeholderSelectionBtn = model.getByName<UIIconButton>("placeholderSelectionButton");
    if (placeholderSelectionBtn) {
      placeholderSelectionBtn.onClick = () => {
        const rawData = localStorage.getItem("alphaCreateData");
        const globalStateData = JSON.parse(rawData);
        const convertedDataToRequiredForm = transformGlobalState(globalStateData);
        const { model } = useAlphaAgentStore.getState();

        const formatTemplateSource = model.getByName<UICheckbox>("formatTemplateSource");
        const formatTemplateNotes = model.getByName<UICheckbox>("formatTemplateNotes");
        const presentationDesign = model.getByName<UIFormatTemplateSelection>("presentationDesign");

        const completeData = addFieldsToData({
          chartObject: convertedDataToRequiredForm,
          customFormatId: presentationDesign.value,
          metadata: {
            source: formatTemplateSource?.value?.length > 0,
            notes: formatTemplateNotes?.value?.length > 0,
          },
          tradingVolume: localStorage.getItem("tradingVolume") === "true",
        });
        localStorage.setItem("ModelDataToBackend", JSON.stringify(completeData));
        // If we came from placeholderPopup, route back there after selection; else route back to alpha agent
        if (fromPlaceholderPopup) {
          localStorage.setItem("PlaceholderPopupFlag", "true");
        } else {
          localStorage.setItem("AlphaAgentPlaceholder", "true");
        }
        Office.context.ui.messageParent("OPEN_PLACEHOLDER");
      };
    }
  };

  const handleTemplateSelect = async (selectedTemplate: any) => {
    try {
      logger.info(`Template selected: ${selectedTemplate.formatName}`);

      const presentationDesign = model.getByName("presentationDesign");
      if (presentationDesign && selectedTemplate) {
        if (selectedTemplate.formatName) {
          (presentationDesign as any).templateName = selectedTemplate.formatName;
        }
        (presentationDesign as any).value = selectedTemplate.id;
        if (selectedTemplate.frontCoverImage?.buffer) {
          try {
            const getImageSrc = bufferToBase64ImagePrimaryTemplate(
              selectedTemplate.frontCoverImage.buffer,
              selectedTemplate.frontCoverImage.contentType
            );
            (presentationDesign as any).image = getImageSrc;
          } catch (conversionError) {
            logger.error("Error converting image:", conversionError);
            (presentationDesign as any).image = undefined;
          }
        } else {
          (presentationDesign as any).image = undefined;
        }

        setCurrentPrimaryTemplate(selectedTemplate);
        setModel(model);

        // Store the selected template in localStorage for persistence
        localStorage.setItem(
          "selectedFormatTemplate",
          JSON.stringify({
            id: selectedTemplate.id,
            formatName: selectedTemplate.formatName,
            image: selectedTemplate.frontCoverImage?.buffer
              ? bufferToBase64ImagePrimaryTemplate(
                  selectedTemplate.frontCoverImage.buffer,
                  selectedTemplate.frontCoverImage.contentType
                )
              : undefined,
          })
        );

        logger.info("Template updated successfully in presentation design and saved to localStorage");
      }
    } catch (error) {
      logger.error("Error handling template selection:", error);
    }
  };

  if (!model) return null;

  return (
    <div className="mt-6 p-4">
      <Renderer model={model} />

      {/* Template Search Popup */}
      <FormatTemplateSearchPopup
        visible={templateSearchVisible}
        onClose={() => setTemplateSearchVisible(false)}
        onTemplateSelect={handleTemplateSelect}
        currentPrimaryTemplateId={currentPrimaryTemplate?.id}
      />
    </div>
  );
}
